import { useEffect, useState, type FormEvent } from 'react';
import type { UserProfile } from '../../../../../shared/types';
import { updateUserProfile } from '../../../services/user.service';
import { BaseModal } from '../../common/BaseModal';
import { SELECT_CHEVRON_STYLE } from '../../../styles/components';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { ErrorMessage } from '../../ErrorMessage';

const COUNTRY_CODES = [
  { code: '+353', flag: '🇮🇪', country: 'Ireland' },
  { code: '+44', flag: '🇬🇧', country: 'United Kingdom' },
  { code: '+1', flag: '🇺🇸', country: 'United States' },
  { code: '+33', flag: '🇫🇷', country: 'France' },
  { code: '+49', flag: '🇩🇪', country: 'Germany' },
  { code: '+39', flag: '🇮🇹', country: 'Italy' },
  { code: '+34', flag: '🇪🇸', country: 'Spain' },
  { code: '+31', flag: '🇳🇱', country: 'Netherlands' },
  { code: '+32', flag: '🇧🇪', country: 'Belgium' },
  { code: '+46', flag: '🇸🇪', country: 'Sweden' },
  { code: '+47', flag: '🇳🇴', country: 'Norway' },
  { code: '+45', flag: '🇩🇰', country: 'Denmark' },
  { code: '+358', flag: '🇫🇮', country: 'Finland' },
  { code: '+41', flag: '🇨🇭', country: 'Switzerland' },
  { code: '+43', flag: '🇦🇹', country: 'Austria' },
  { code: '+48', flag: '🇵🇱', country: 'Poland' },
  { code: '+61', flag: '🇦🇺', country: 'Australia' },
  { code: '+64', flag: '🇳🇿', country: 'New Zealand' },
  { code: '+81', flag: '🇯🇵', country: 'Japan' },
  { code: '+82', flag: '🇰🇷', country: 'South Korea' },
  { code: '+91', flag: '🇮🇳', country: 'India' },
  { code: '+55', flag: '🇧🇷', country: 'Brazil' },
  { code: '+52', flag: '🇲🇽', country: 'Mexico' },
];

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile;
  companyId: string;
  canEditEmail: boolean; // true for company admin, false for manager
  onSuccess: () => void;
}

export function EditUserModal({ isOpen, onClose, user, companyId, canEditEmail, onSuccess }: EditUserModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+353');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setEmail(user.email || '');
      const knownCodes = new Set(COUNTRY_CODES.map((c) => c.code));
      const fallback = user.countryCode && knownCodes.has(user.countryCode) ? user.countryCode : '+353';
      setCountryCode(fallback);
      setPhoneNumber(user.phoneNumber || '');
      setError(null);
    }
  }, [isOpen, user]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required.');
      return;
    }
    if (canEditEmail && !email.trim()) {
      setError('Email is required.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await updateUserProfile({
        targetUserId: user.uid,
        companyId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.trim(),
        countryCode,
        ...(canEditEmail ? { email: email.trim() } : {}),
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit ${user.userType === 'manager' ? 'Manager' : 'Staff'} Profile`}
      size="small"
      closeOnEscape={!submitting}
      closeOnBackdropClick={!submitting}
    >
      <div style={{ marginBottom: '16px', padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', fontSize: '14px', color: '#6b7280' }}>
        Role: <strong style={{ color: '#111827' }}>{user.userType === 'manager' ? 'Manager' : 'Staff'}</strong>
      </div>

      {error && (
        <div style={{ marginBottom: '16px' }}>
          <ErrorMessage message={error} onDismiss={() => setError(null)} />
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>First Name <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              disabled={submitting}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Last Name <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              disabled={submitting}
              style={inputStyle}
            />
          </div>
        </div>

        {canEditEmail && (
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Email <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
              style={inputStyle}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Changing the email will update the user's login credentials.
            </p>
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>Phone Number</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              disabled={submitting}
              style={{ ...inputStyle, ...SELECT_CHEVRON_STYLE, width: '140px', flexShrink: 0, paddingRight: '36px' }}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.code}
                </option>
              ))}
            </select>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d\s\-().]/g, ''))}
              placeholder="86 123 4567"
              disabled={submitting}
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>
        </div>

        <ModalButtonGroup
          onCancel={onClose}
          submitType="submit"
          submitLabel="Save Changes"
          isLoading={submitting}
          loadingLabel="Saving..."
        />
      </form>
    </BaseModal>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  outline: 'none',
  boxSizing: 'border-box',
};
